import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileCheck2, MessageCircle } from "lucide-react";
import Reveal from "../components/Reveal";
import Faq from "../components/Faq";
import RetirementCheck from "../components/RetirementCheck";
import { CATEGORIES, openTriage } from "../data/office";
import { AREA_PAGES } from "../data/content";
import { TRIAGE } from "../data/triage";

const STEPS = [
  ["Triagem pelo site", "Você responde algumas perguntas rápidas e envia pelo WhatsApp."],
  ["Análise do caso", "Avaliamos as informações e os documentos e explicamos os caminhos possíveis."],
  ["Estratégia e acompanhamento", "Definida a estratégia, cuidamos de tudo e mantemos você informado(a)."],
];

function setMeta(title, description) {
  document.title = title;
  const el = document.querySelector('meta[name="description"]');
  if (el && description) el.setAttribute("content", description);
}

export default function AreaPage() {
  const { slug } = useParams();
  const cat = CATEGORIES.find((c) => c.slug === slug);
  const page = AREA_PAGES[slug];
  const triage = TRIAGE[slug];

  useEffect(() => {
    if (!page) return;
    const prevTitle = document.title;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    setMeta(`${page.seoTitle} | AMN Advogadas`, page.seoDescription);
    return () => setMeta(prevTitle, prevDesc);
  }, [page]);

  if (!cat || !page) return <Navigate to="/" replace />;
  const Icon = cat.icon;

  return (
    <main data-testid={`area-page-${slug}`} className="bg-navy-900 pt-[72px]">
      <section className="grain relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-[-10%] h-[480px] w-[480px] rounded-full bg-gold-500/10 blur-[140px]" />
        <div className="relative mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-28">
          <Link to="/#areas" className="inline-flex items-center gap-2 text-sm text-mist transition-colors hover:text-gold-400">
            <ArrowLeft className="h-4 w-4" /> Todas as áreas
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold-500/30 bg-navy-800 text-gold-400">
              <Icon className="h-5 w-5" />
            </span>
            <p className="font-mono-meta text-[11px] uppercase tracking-[0.35em] text-gold-400">Área de atuação</p>
          </div>
          <h1 className="font-display mt-6 text-5xl font-medium leading-[1.02] text-cream sm:text-6xl lg:text-7xl">
            {cat.label}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-mist">{cat.tagline}</p>
          <div className="mt-6 max-w-3xl space-y-4 text-[15px] leading-relaxed text-mist/90">
            {page.intro.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
          <button
            data-testid="area-cta-top"
            onClick={() => openTriage({ title: cat.label, area: slug })}
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold-500 px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-950 transition-transform duration-300 hover:scale-[1.03]"
          >
            <MessageCircle className="h-4 w-4" /> Falar sobre o meu caso
          </button>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-medium text-cream sm:text-4xl">
            Como podemos <em className="gold-text">ajudar</em>
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          {cat.solutions.map((s, i) => {
            const SIcon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.04}>
                <article className="flex h-full flex-col rounded-2xl border border-navy-600/60 bg-card p-6">
                  <div className="flex items-center gap-3">
                    <SIcon className="h-5 w-5 text-gold-400" strokeWidth={1.75} />
                    <h3 className="font-display text-xl font-semibold text-cream">{s.title}</h3>
                  </div>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-mist">{s.desc}</p>
                  <button
                    onClick={() => openTriage({ title: s.title, area: slug })}
                    className="mt-5 inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold-400 hover:gap-3"
                  >
                    Consultar sobre este tema <ArrowUpRight className="h-4 w-4" />
                  </button>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="mx-auto grid max-w-5xl grid-cols-1 gap-10 px-5 py-16 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-display text-3xl font-medium text-cream">Como funciona</h2>
          <ol className="mt-8 space-y-6">
            {STEPS.map(([t, d], i) => (
              <li key={t} className="flex gap-4">
                <span className="font-mono-meta flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold-500/40 text-sm text-gold-400">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-cream">{t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-mist">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>
        {triage && (
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-gold-500/25 bg-navy-800 p-7">
              <h2 className="flex items-center gap-2.5 font-display text-2xl font-semibold text-cream">
                <FileCheck2 className="h-5 w-5 text-gold-400" /> Documentos para separar
              </h2>
              <ul className="mt-5 space-y-2.5 text-sm text-mist">
                {triage.documents.map((d) => (
                  <li key={d} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rotate-45 bg-gold-500" />
                    {d}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-mist/70">Não tem tudo? Envie o que tiver e orientamos o restante.</p>
            </div>
          </Reveal>
        )}
      </section>

      {slug === "previdenciario" && <RetirementCheck compact />}

      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-medium text-cream sm:text-4xl">
            Perguntas <em className="gold-text">frequentes</em>
          </h2>
        </Reveal>
        <div className="mt-8">
          <Faq items={page.faqs} testId={`faq-${slug}`} />
        </div>
        <p className="mt-6 text-xs leading-relaxed text-mist/60">
          Conteúdo informativo, que não substitui a análise individual do seu caso por um(a) advogado(a).
        </p>
      </section>
    </main>
  );
}
